import React, { useState, useCallback } from "react";
import MainCalculatorsList from "./MainCalculators";
import SharedHelmet from "../Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
const TITLE = "Calculatrice de pression - Calculer la pression, la force ou l'aire";
const DESCRIPTION =
  "Calculatrice de pression - Calculer la pression, la force ou l'aire. Calculateur de physique en francais avec resultat rapide, unites claires et aide pratique pour exercices et revisions.";
const KEYWORDS =
  "pressure, calculatrice physique, formule physique, exercices de physique, calcul en ligne";
const OG_URL = "https://do-calculate.com/calculator/fr/pressure";

function PressureCalculator() {
  const [pressure, setPressure] = useState("");
  const [force, setForce] = useState("");
  const [area, setArea] = useState("");
  const [calculationMode, setCalculationMode] = useState("calculatePressure");
  const [error, setError] = useState("");
  const [showSteps, setShowSteps] = useState(false);

  const updateCalculations = useCallback(() => {
    setError("");
    const pressureValue = parseFloat(pressure) || 0;
    const forceValue = parseFloat(force) || 0;
    const areaValue = parseFloat(area) || 0;

    switch (calculationMode) {
      case "calculatePressure":
        if (forceValue > 0 && areaValue > 0) {
          const calculatedPressure = forceValue / areaValue;
          setPressure(Math.round(calculatedPressure * 100) / 100);
        } else {
          setError("Veuillez entrer la force et l'aire correctement");
        }
        break;
      case "calculateForce":
        if (pressureValue > 0 && areaValue > 0) {
          const calculatedForce = pressureValue * areaValue;
          setForce(Math.round(calculatedForce * 100) / 100);
        } else {
          setError("Veuillez entrer la pression et l'aire correctement");
        }
        break;
      case "calculateArea":
        if (pressureValue > 0 && forceValue > 0) {
          const calculatedArea = forceValue / pressureValue;
          setArea(Math.round(calculatedArea * 100) / 100);
        } else {
          setError("Veuillez entrer la pression et la force correctement");
        }
        break;
      default:
        break;
    }
  }, [pressure, force, area, calculationMode]);

  const handleCalculateClick = () => {
    updateCalculations();
    setShowSteps(true);
  };

  const handlePressureChange = (e) => setPressure(e.target.value);
  const handleForceChange = (e) => setForce(e.target.value);
  const handleAreaChange = (e) => setArea(e.target.value);
  const handleModeChange = (e) => setCalculationMode(e.target.value);

  const getSolutionSteps = () => {
    switch (calculationMode) {
      case "calculatePressure":
        return (
          <div className="alert alert-info">
            <h4>Étapes de calcul de la pression:</h4>
            <ol>
              <li>Entrez la force (en newtons) dans le champ approprié.</li>
              <li>Entrez l'aire (en mètres carrés) dans le champ approprié.</li>
              <li>Cliquez sur le bouton "Calculer" pour obtenir la pression.</li>
              <li>La pression est égale à la force / l'aire.</li>
            </ol>
          </div>
        );
      case "calculateForce":
        return (
          <div className="alert alert-info">
            <h4>Étapes de calcul de la force:</h4>
            <ol>
              <li>Entrez la pression (en pascals) dans le champ approprié.</li>
              <li>Entrez l'aire (en mètres carrés) dans le champ approprié.</li>
              <li>Cliquez sur le bouton "Calculer" pour obtenir la force.</li>
              <li>La force est égale à la pression × l'aire.</li>
            </ol>
          </div>
        );
      case "calculateArea":
        return (
          <div className="alert alert-info">
            <h4>Étapes de calcul de l'aire:</h4>
            <ol>
              <li>Entrez la pression (en pascals) dans le champ approprié.</li>
              <li>Entrez la force (en newtons) dans le champ approprié.</li>
              <li>Cliquez sur le bouton "Calculer" pour obtenir l'aire.</li>
              <li>L'aire est égale à la force / la pression.</li>
            </ol>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "50px" }}
          >
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <p>
                  La calculatrice de pression vous permet d'effectuer trois types de calculs 
                  en fonction des valeurs saisies. Choisissez le type de calcul approprié et 
                  entrez les valeurs disponibles pour calculer la valeur manquante avec 
                  précision et facilité.
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Trouver la pression connaissant la force et l'aire
                </h2>
                <p>
                  Si vous connaissez la force (en newtons) et l'aire (en mètres carrés), 
                  vous pouvez calculer la pression (en pascals) en utilisant la formule suivante:
                </p>
                <p>
                  <b>Pression = Force / Aire</b>
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Trouver la force connaissant la pression et l'aire
                </h2>
                <p>
                  Si vous connaissez la pression (en pascals) et l'aire (en mètres carrés), 
                  vous pouvez calculer la force (en newtons) en utilisant la formule suivante:
                </p>
                <p>
                  <b>Force = Pression × Aire</b></p>
              </div>
            </div>
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>Calculatrice de pression</h2>
                </div>
                <hr />
                <div>
                  <div className="form-group">
                    <label htmlFor="mode" style={{marginRight: "10px"}}>Mode de calcul:</label>
                    <select
                      id="mode"
                      value={calculationMode}
                      onChange={handleModeChange}
                      className="form-control"
                    >
                      <option value="calculatePressure">Calculer la pression</option>
                      <option value="calculateForce">Calculer la force</option>
                      <option value="calculateArea">Calculer l'aire</option>
                    </select>
                  </div>

                  {(calculationMode === "calculatePressure" || calculationMode === "calculateForce" || calculationMode === "calculateArea") && (
                    <div className="form-group">
                      <label htmlFor="pressure" style={{marginRight: "10px"}}>Pression (Pa):</label>
                      <input
                        id="pressure"
                        type="number"
                        value={pressure}
                        onChange={handlePressureChange}
                        className="form-control"
                        placeholder="Entrez la pression"
                      />
                    </div>
                  )}

                  {(calculationMode === "calculatePressure" || calculationMode === "calculateForce" || calculationMode === "calculateArea") && (
                    <div className="form-group">
                      <label htmlFor="force" style={{marginRight: "10px"}}>Force (N):</label>
                      <input
                        id="force"
                        type="number"
                        value={force}
                        onChange={handleForceChange}
                        className="form-control"
                        placeholder="Entrez la force"
                      />
                    </div>
                  )}

                  {(calculationMode === "calculatePressure" || calculationMode === "calculateForce" || calculationMode === "calculateArea") && (
                    <div className="form-group">
                      <label htmlFor="area" style={{marginRight: "10px"}}>Aire (m²):</label>
                      <input
                        id="area"
                        type="number"
                        value={area}
                        onChange={handleAreaChange}
                        className="form-control"
                        placeholder="Entrez l'aire"
                      />
                    </div>
                  )}

                  <button
                    className="btn btn-success"
                    style={{ fontSize: "20px" }}
                    onClick={handleCalculateClick}
                  >
                    Calculer
                  </button>
                  {error && <p className="p-3 mb-2 bg-warning text-white mt-3">Erreur: {error}</p>}
                  {showSteps && getSolutionSteps()}
                </div>
              </div>
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList />
          <br />
        </div>
      </div>
    </div>
  );
}
export default PressureCalculator;
